/**
 * Camera sync coordinator.
 *
 * The store keeps shadow copies of the live OrbitControls state
 * (`cameraPosition`, `cameraTarget`, `cameraUp`) for hydration, persistence,
 * and React subscribers. Those fields must stay consistent with the live
 * controls within a synchronous boundary, or any reader that samples during
 * a lag window — notably `setWorkspaceMode`'s synchronous side effects —
 * sees stale data and writes it back, producing apparent "camera jumps".
 *
 * This module owns the single live → shadow write path:
 *   - `registerCameraSync` is called once by `CameraRig`'s init effect with
 *     a function that reads the current live pose.
 *   - `markCameraDirty` queues a debounced write (used by ongoing gestures
 *     that settle — orbit end, fly-mode key release, etc.).
 *   - `flushCameraSync` cancels any pending write and syncs immediately
 *     (used at mode-switch entry, transition completion, and after any
 *     synchronous `applyState` that must see a fresh shadow).
 *
 * Mid-transition flushes are a no-op by design: `CameraTransitionDriver`
 * owns the final pose write when `tx` completes, and flushing an
 * intermediate lerped pose would snapshot a frame that isn't where the
 * user intended to land.
 *
 * Module-level state mirrors the pattern used by `cameraTransition.ts`
 * so non-React callers (store actions, `cameraViews` utilities) can reach
 * the flush without hooks.
 */

import { useStore } from "../app/store.ts";
import { isCameraTransitioning } from "./cameraTransition.ts";

export interface LiveCameraState {
  position: [number, number, number];
  target: [number, number, number];
  /** Camera up vector, or null to leave the shadow's current `cameraUp` as-is. */
  cameraUp: [number, number, number] | null;
}

/**
 * Reads the current live camera pose. Returns null when controls aren't
 * mounted yet (pre-Canvas) or have been torn down — the shadow store is
 * authoritative in that window.
 */
export type LiveCameraRead = () => LiveCameraState | null;

const DEBOUNCE_MS = 80;

let readLive: LiveCameraRead | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;
const motionListeners = new Set<() => void>();
const gestureStartListeners = new Set<() => void>();
// Monotonic token; bumped on every supersede so a stray in-flight callback
// (e.g. a future microtask-based path) can detect it has been invalidated.
// With plain `setTimeout` cancellation is synchronous, so today this is
// defence-in-depth rather than load-bearing.
let generation = 0;

export function registerCameraSync(fn: LiveCameraRead): () => void {
  readLive = fn;
  return () => {
    if (readLive === fn) {
      cancelPending();
      readLive = null;
    }
  };
}

/**
 * Schedule a sync to fire `DEBOUNCE_MS` after the last call. Additional calls
 * reset the timer — only the final state gets written. Safe to call before
 * registration; the scheduled sync will no-op gracefully.
 */
export function markCameraDirty(): void {
  cancelPending();
  const gen = ++generation;
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    applyNow(gen);
  }, DEBOUNCE_MS);
  for (const fn of motionListeners) fn();
}

/**
 * Subscribe to live camera motion events. The listener fires synchronously
 * from every `markCameraDirty` call (i.e., every OrbitControls change frame
 * during a drag, plus discrete moves from fly mode and keyboard zoom), so
 * subscribers can drive UI that needs to react to active manipulation
 * before the debounced shadow store write lands.
 */
export function subscribeCameraMotion(listener: () => void): () => void {
  motionListeners.add(listener);
  return () => {
    motionListeners.delete(listener);
  };
}

/**
 * Subscribe to user-initiated camera gestures. Distinct from
 * `subscribeCameraMotion`, which fires on every change event including the
 * damping tail and any post-settle echoes that an unrelated invalidate (e.g.
 * a Zustand store update) wakes the render loop into producing. A "gesture
 * start" is a discrete user action: OrbitControls mouse-down, a fly-mode key
 * release that produced movement, or a keyboard zoom keypress.
 */
export function subscribeCameraGestureStart(
  listener: () => void,
): () => void {
  gestureStartListeners.add(listener);
  return () => {
    gestureStartListeners.delete(listener);
  };
}

/** Fire gesture-start listeners. Called from user-input paths. */
export function notifyCameraGestureStart(): void {
  for (const fn of gestureStartListeners) fn();
}

/**
 * Cancel any pending debounced sync and write the live pose to the store
 * synchronously. No-op if no live reader is registered or a camera
 * transition is in flight (the transition owns its own final flush).
 */
export function flushCameraSync(): void {
  cancelPending();
  applyNow(++generation);
}

/** True when a debounced sync is scheduled but not yet fired. */
export function isCameraSyncPending(): boolean {
  return pendingTimer !== null;
}

function cancelPending(): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
}

function applyNow(gen: number): void {
  if (gen !== generation) return;
  if (!readLive) return;
  // Intermediate frames of an axis-view transition aren't a valid shadow
  // value; the driver calls flushCameraSync itself on completion.
  if (isCameraTransitioning()) return;
  const live = readLive();
  if (!live) return;
  const patch: {
    cameraPosition: [number, number, number];
    cameraTarget: [number, number, number];
    cameraUp?: [number, number, number];
  } = {
    cameraPosition: live.position,
    cameraTarget: live.target,
  };
  if (live.cameraUp) patch.cameraUp = live.cameraUp;
  useStore.setState(patch);
}

/**
 * Test-only reset: clears any registration, pending timer, and generation
 * so each test starts from a clean slate. Not exported for production use.
 */
export function __resetCameraSyncForTests(): void {
  cancelPending();
  readLive = null;
  generation = 0;
  motionListeners.clear();
  gestureStartListeners.clear();
}
